import classNames from "classnames";
import { ExtendedAccount } from "@common/types/reports/types";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { Menu } from "@design-system/Menu/Menu.lib";
import { useState } from "react";
import { useOverlays } from "contexts/OverlayContext";
import { useCanPerformWorkspaceAction } from "@hooks/permissions/useCanPerformWorkspaceAction";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { viewOnlyTooltipReason } from "@hooks/permissions/useViewOnlyTooltipReason";
import { WorkspaceAction } from "@common/userWorkspaceRoles";
import { getAccountRoleReassignmentBlockMessage } from "@common/constants/domains/accounting/accountRole";
import {
    getArchiveBlockReason,
    getDeleteBlockReason,
    getMergeBlockReason,
} from "./accountActionEligibility";
import { RemoveOpeningBalanceModal } from "./RemoveOpeningBalanceModal";
import { useBusinessNamesFor } from "./reachCopy";
import {
    MEMBER_RESTRICTION_TOOLTIP,
    MERGE_ACCOUNT_SINGLE_ENTITY_TOOLTIP,
    REASSIGN_ROLE_OUTCOME_TOOLTIP,
    mergeActionTooltip,
    REASSIGN_ROLE_SINGLE_ENTITY_TOOLTIP,
} from "./constants";
import { PermissionGuardedMenuItem } from "./PermissionGuardedMenuItem";
import { RevertToDefaultNameMenuItem } from "./RevertToDefaultNameMenuItem";
import { EditorFocusTarget } from "@components/settings/ChartOfAccountsTemplates/editor/editorBodyTypes";
import { useGoToAccountRules } from "@hooks/useGoToAccountRules";
import styles from "./styles.module.scss";

interface Props {
    account: ExtendedAccount;
    similarAccounts: ExtendedAccount[];
    openEditorForRow: (target: EditorFocusTarget) => void;
}

export const ChartOfAccountsItemActions = ({
    account,
    similarAccounts,
    openEditorForRow,
}: Readonly<Props>) => {
    const { openOverlay } = useOverlays();
    const goToAccountRules = useGoToAccountRules();
    const [
        isRemoveOpeningBalanceModalOpen,
        setIsRemoveOpeningBalanceModalOpen,
    ] = useState(false);

    const businessNamesFor = useBusinessNamesFor();

    // Any refusing constituent blocks the whole row: the entity filter is the
    // only way to narrow scope, so a partially-blocked row is offered no click.
    const canDisable = similarAccounts.every(
        (acc) => getArchiveBlockReason(acc) === null,
    );

    const canDelete = similarAccounts.every(
        (acc) => getDeleteBlockReason(acc) === null,
    );

    const buildRefusalTooltip = (
        reasonFor: (account: ExtendedAccount) => string | null,
    ): string => {
        const seenEntityIds = new Set<number>();
        const lines: string[] = [];
        for (const acc of similarAccounts) {
            if (seenEntityIds.has(acc.entityId)) {
                continue;
            }
            const reason = reasonFor(acc);
            if (reason == null) {
                continue;
            }
            seenEntityIds.add(acc.entityId);
            const [name] = businessNamesFor([acc.entityId]);
            lines.push(name ? `${name}: ${reason}` : reason);
        }
        return lines.join("\n");
    };

    const openingBalanceTargets = similarAccounts
        .filter((acc) => acc.hasOpeningBalance)
        .map((acc) => ({
            entityId: acc.entityId,
            accountId: acc.id,
        }));

    const handleDisableAccount = () => {
        openOverlay<"disableChartOfAccounts">("disableChartOfAccounts", {
            accounts: similarAccounts,
        });
    };

    const handleEnableAccount = () => {
        openOverlay<"enableChartOfAccounts">("enableChartOfAccounts", {
            accounts: similarAccounts,
        });
    };

    const handleDeleteAccount = () => {
        openOverlay<"deleteChartOfAccounts">("deleteChartOfAccounts", {
            accounts: similarAccounts,
        });
    };

    const handleGoToRules = () => {
        const constituents =
            similarAccounts.length > 0 ? similarAccounts : [account];
        goToAccountRules({
            accountIds: constituents.map((acc) => acc.id),
            entityIds: constituents.map((acc) => acc.entityId),
        });
    };

    const handleMergeAccount = () => {
        openOverlay<"mergeChartOfAccounts">("mergeChartOfAccounts", {
            account,
        });
    };

    const handleReassignRole = () => {
        openOverlay<"reassignAccountRole">("reassignAccountRole", {
            account,
        });
    };

    const canEditCoA = useCanPerformWorkspaceAction(WorkspaceAction.COA_EDIT);
    const { isViewOnly } = useViewOnlyRefusal();

    const mergeBlockReason = getMergeBlockReason(account);
    const isMultiBusinessRow = similarAccounts.length > 1;
    const canMerge = canEditCoA && !mergeBlockReason && !isMultiBusinessRow;

    // Visibility follows any constituent, not just the representative: a
    // role-less representative can sit in front of a holder.
    const showReassignRole = similarAccounts.some((acc) => acc.role != null);
    const reassignRoleBlockMessage = similarAccounts.reduce<string | null>(
        (message, acc) =>
            message ?? getAccountRoleReassignmentBlockMessage(acc.role),
        null,
    );

    const mergeTooltipMessage = () => {
        if (!canEditCoA) {
            return MEMBER_RESTRICTION_TOOLTIP;
        }
        if (mergeBlockReason) {
            return mergeBlockReason;
        }
        return MERGE_ACCOUNT_SINGLE_ENTITY_TOOLTIP;
    };

    const reassignTooltipMessage = () =>
        canEditCoA
            ? REASSIGN_ROLE_SINGLE_ENTITY_TOOLTIP
            : MEMBER_RESTRICTION_TOOLTIP;

    const canModify = canEditCoA && !account.isDisabled;

    const editTooltipMessage = () =>
        canEditCoA
            ? "Account is archived and cannot be edited"
            : MEMBER_RESTRICTION_TOOLTIP;

    const deleteTooltipMessage = () => {
        if (!canEditCoA) {
            return MEMBER_RESTRICTION_TOOLTIP;
        }
        return (
            buildRefusalTooltip((acc) => getDeleteBlockReason(acc)) ||
            "Account cannot be deleted"
        );
    };

    return (
        <>
            <div
                className={classNames(
                    styles.columnContent,
                    styles.actionsColumn,
                )}
            >
                <Menu.More className={viewTableStyles.showOnHover}>
                    <Menu.Item
                        disabled={!canModify}
                        disabledReason={viewOnlyTooltipReason({
                            isViewOnly,
                            specificReason: editTooltipMessage(),
                        })}
                        onClick={() =>
                            openEditorForRow({
                                kind: "account",
                                id: account.id,
                            })
                        }
                    >
                        Edit
                    </Menu.Item>
                    <RevertToDefaultNameMenuItem
                        account={account}
                        similarAccounts={similarAccounts}
                        canEditCoA={canEditCoA}
                    />
                    <Menu.Item onClick={handleGoToRules}>Go to rules</Menu.Item>
                    {openingBalanceTargets.length > 0 && (
                        <PermissionGuardedMenuItem
                            isAllowed={canEditCoA}
                            onClick={() =>
                                setIsRemoveOpeningBalanceModalOpen(true)
                            }
                        >
                            Remove opening balance
                        </PermissionGuardedMenuItem>
                    )}
                    {account.isDisabled && (
                        <PermissionGuardedMenuItem
                            isAllowed={canEditCoA}
                            onClick={handleEnableAccount}
                        >
                            Enable
                        </PermissionGuardedMenuItem>
                    )}
                    {!account.isDisabled && !canDisable && (
                        <Menu.Item
                            disabled
                            disabledReason={viewOnlyTooltipReason({
                                isViewOnly,
                                specificReason:
                                    buildRefusalTooltip(
                                        getArchiveBlockReason,
                                    ) || "Account cannot be archived",
                            })}
                        >
                            Archive
                        </Menu.Item>
                    )}
                    {canDisable && !account.isDisabled && (
                        <PermissionGuardedMenuItem
                            isAllowed={canEditCoA}
                            onClick={handleDisableAccount}
                        >
                            Archive
                        </PermissionGuardedMenuItem>
                    )}

                    <Menu.Item
                        disabled={!canMerge}
                        onClick={handleMergeAccount}
                        tooltip={viewOnlyTooltipReason({
                            isViewOnly,
                            specificReason: mergeActionTooltip(account.role),
                        })}
                        disabledReason={viewOnlyTooltipReason({
                            isViewOnly,
                            specificReason: mergeTooltipMessage(),
                        })}
                    >
                        Merge
                    </Menu.Item>

                    {showReassignRole &&
                        (isMultiBusinessRow ||
                        reassignRoleBlockMessage !== null ? (
                            <Menu.Item
                                disabled
                                disabledReason={viewOnlyTooltipReason({
                                    isViewOnly,
                                    specificReason:
                                        reassignRoleBlockMessage ??
                                        reassignTooltipMessage(),
                                })}
                            >
                                Reassign role
                            </Menu.Item>
                        ) : (
                            <PermissionGuardedMenuItem
                                isAllowed={canEditCoA}
                                onClick={handleReassignRole}
                                allowedTooltip={REASSIGN_ROLE_OUTCOME_TOOLTIP}
                            >
                                Reassign role
                            </PermissionGuardedMenuItem>
                        ))}

                    <Menu.Item
                        destructive
                        disabled={!(canDelete && canEditCoA)}
                        disabledReason={viewOnlyTooltipReason({
                            isViewOnly,
                            specificReason: deleteTooltipMessage(),
                        })}
                        onClick={handleDeleteAccount}
                    >
                        Delete
                    </Menu.Item>
                </Menu.More>
            </div>
            {openingBalanceTargets.length > 0 && (
                <RemoveOpeningBalanceModal
                    show={isRemoveOpeningBalanceModalOpen}
                    onHide={() => setIsRemoveOpeningBalanceModalOpen(false)}
                    accountName={account.name}
                    targets={openingBalanceTargets}
                    rowEntityIds={Array.from(
                        new Set(similarAccounts.map((acc) => acc.entityId)),
                    )}
                />
            )}
        </>
    );
};
